// Image-search region geometry (pure functions). Port of
// apps/web/lib/region-geometry.ts. Backend YOLO RegionBox is in *original-pixel*
// coordinates; the crop overlay works in *percentage* coordinates (0–100,
// relative to the image itself), which are render-size independent.

import type { RegionBox } from "@kupikupi/api";

/** Percentage rect (0–100, relative to the image). Render-size independent. */
export interface PercentRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The downscaled image's pixel size (returned by downscaleForImageSearch) =
 *  the coordinate system the RegionBox lives in. */
export interface NaturalDims {
  naturalWidth: number;
  naturalHeight: number;
}

/**
 * Original-pixel box → percentage rect (relative to the downscaled image). This
 * is the key to dropping the DOM `<img>` onLoad dependency: the RegionBox and
 * the downscaled size are both known synchronously at detect time, so the
 * percentage conversion is independent of display size. Invalid dims (≤0)
 * return a zero rect to avoid divide-by-zero.
 */
export function naturalRegionToPercentRect(region: RegionBox, dims: NaturalDims): PercentRect {
  if (dims.naturalWidth <= 0 || dims.naturalHeight <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  return {
    x: (region.x / dims.naturalWidth) * 100,
    y: (region.y / dims.naturalHeight) * 100,
    width: (region.w / dims.naturalWidth) * 100,
    height: (region.h / dims.naturalHeight) * 100,
  };
}

/**
 * Is the current percentage selection this box (tolerance 0.5%)? → that box is
 * already wrapped by the highlight frame, so the overlay stops drawing its dot.
 */
export function percentRectMatches(a: PercentRect, b: PercentRect): boolean {
  return (
    Math.abs(a.x - b.x) < 0.5 &&
    Math.abs(a.y - b.y) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

/**
 * Point-in-box hit test (percentage coordinates): click point (0–100) → hit box
 * index, -1 if none. Overlaps resolve to the smallest (most specific) box.
 */
export function hitTestRegionPercent(
  pctX: number,
  pctY: number,
  regions: RegionBox[],
  dims: NaturalDims,
): number {
  let best = -1;
  let bestArea = Number.POSITIVE_INFINITY;
  for (let i = 0; i < regions.length; i++) {
    const r = naturalRegionToPercentRect(regions[i] as RegionBox, dims);
    if (r.width <= 0 || r.height <= 0) continue;
    const inside = pctX >= r.x && pctX < r.x + r.width && pctY >= r.y && pctY < r.y + r.height;
    if (!inside) continue;
    const area = r.width * r.height;
    if (area < bestArea) {
      bestArea = area;
      best = i;
    }
  }
  return best;
}

/**
 * Default selected box = the candidate with the largest original-pixel area
 * (the biggest / most prominent product). Empty array → null; ties take the
 * first (the backend box order is stable).
 */
export function pickLargestRegion(regions: RegionBox[]): RegionBox | null {
  let best: RegionBox | null = null;
  let bestArea = -1;
  for (const region of regions) {
    const area = region.w * region.h;
    if (area > bestArea) {
      bestArea = area;
      best = region;
    }
  }
  return best;
}

/** Clamp a percentage rect into the [0,100] box (keep width/height positive). */
export function clampPercentRect(r: PercentRect): PercentRect {
  const x = Math.min(Math.max(r.x, 0), 100);
  const y = Math.min(Math.max(r.y, 0), 100);
  const width = Math.min(Math.max(r.width, 0), 100 - x);
  const height = Math.min(Math.max(r.height, 0), 100 - y);
  return { x, y, width, height };
}
